// Home VL — el tablero del Portal Admin VentasLuga (F3 · pista Retail 🔵).
// Mockup: `surekeep/mockups/backoffice/portal-admin-vl.html` (`origin/feature/admin-vl-pulido`).
//
// Endpoints propios: `sales/kpis` y `cancellations`; rankings, stock y promos reusan los de sus pantallas.
// La meta de la red y las alertas del piso se arman aquí con lo ya cargado; un hueco nunca se pinta como 0.
import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowRight, FileText } from 'lucide-react';
import { Button, ErrorState, Skeleton, usePermissions } from '@/kit';
import { cn } from '@/lib/cn';
import { ScreenBand } from '@/shared/patterns/ScreenBand';
import { ScreenControls } from '@/shared/patterns/AppShell/screenControls';
import { useHomeCancellations7d, useHomeSalesKpis } from './api/useHomeVl';
import { useTiendas } from '../tiendas/api/useTiendas';
import { useSellers } from '../vendedores/api/useVendedores';
import { hasSales } from '../lib/roster';
import { useInventarioCritical } from '../inventario/api/useInventarioLens';
import { LaCentralDialog, useLaCentral } from '../inventario/components/LaCentralAction';
import { useInventoryAbilities } from '../inventario/lib/inventoryPermissions';
import { usePromotionsConfigured, useSalePromotions } from '../promotions/api/usePromotions';
import { PROMOTIONS_PERMISSIONS } from '../promotions/permissions';
import {
    activeShare,
    baselineChange,
    formatMoneyShort,
    formatPercentDelta,
    goalPacePerHourCents,
    goalRemainingCents,
    goalSurplusCents,
    hoursToClosing,
    mixWithShare,
    networkGoal,
    networkGoalProgress,
    nowHhMm,
    surekeepMixShare,
} from './lib/homeMetrics';
import { HomeRankings } from './components/HomeRankings';
import { HomeAlerts, HomeStock } from './components/HomePanels';
import { ProgressMini } from './components/ProgressMini';
import { buildFloorAlerts, type FloorAlertSource, type FloorAlertsResult } from './lib/floorAlerts';

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
const money = (cents: number) => MXN.format(cents / 100);

/** Por qué cada hueco de la barra de KPIs está vacío. Se muestra, no solo se pone en el `title`. */
const GOAL_NONE = 'Ninguna tienda tiene meta este mes.';
const GOAL_UNAVAILABLE = 'No se pudieron cargar las metas de las tiendas.';
const GOAL_SOLD_UNKNOWN = 'Alguna tienda con meta no trae lo vendido hoy.';
const FLOOR_NO_SOURCE = 'El silo todavía no cuenta tiendas ni vendedores con venta hoy.';
const TICKET_DELTA_NO_BASELINE = 'Hace 7 días no hubo ventas contra las cuales comparar el ticket.';
const BASELINE_UNITS_NO_SOURCE = 'El silo manda el monto de la línea base, no sus piezas.';

const FINANCIER_LABEL: Record<string, string> = {
    SUREKEEP: 'SureKeep',
    PAYJOY: 'PayJoy',
    CREDIYA: 'CrediYa',
    LESPAGO: 'LesPago',
    INNOVA: 'Innova',
    CASH: 'Contado',
};

const FINANCIER_BAR: Record<string, string> = {
    SUREKEEP: 'bg-success',
    PAYJOY: 'bg-info',
    CREDIYA: 'bg-warning',
    LESPAGO: 'bg-[hsl(220_30%_60%)]',
    INNOVA: 'bg-[hsl(280_40%_60%)]',
    CASH: 'bg-muted-foreground/60',
};

function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}

/** Estado de una fuente de alertas a partir de su query. */
function sourceOf<T>(query: { error: unknown; isPending: boolean }, data: T | undefined) {
    if (query.error) return { status: 'error' } as const;
    if (data === undefined || query.isPending) return { status: 'loading' } as const;
    return { status: 'ready', data } as const;
}

/**
 * KPI de la barra superior: label, valor y sub en una fila alineada a la línea base.
 * `pending` pinta el guion, pone la razón en el `title` y `pendingLabel` en el sub, en cursiva.
 */
function Kpi({
    label,
    value,
    sub,
    valueClass,
    pending,
    pendingLabel = 'sin fuente',
    children,
}: {
    label: string;
    value: React.ReactNode;
    sub: React.ReactNode;
    valueClass?: string;
    pending?: string;
    pendingLabel?: string;
    children?: React.ReactNode;
}) {
    return (
        <div
            className="flex flex-none items-baseline gap-1.5 border-r border-border px-3.5 last:border-r-0"
            title={pending}
        >
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                {label}
            </span>
            <span
                className={cn(
                    'text-[14px] font-semibold tabular-nums',
                    pending ? 'text-muted-foreground' : valueClass,
                )}
            >
                {pending ? '—' : value}
            </span>
            {children}
            <span
                className={cn(
                    'whitespace-nowrap text-[10px] text-muted-foreground',
                    pending && 'italic',
                )}
            >
                {pending ? pendingLabel : sub}
            </span>
        </div>
    );
}

function Card({
    title,
    linkTo,
    linkLabel,
    meta,
    children,
    className,
}: {
    title: string;
    linkTo?: string;
    linkLabel?: string;
    meta?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <section
            className={cn(
                'flex min-h-0 flex-col rounded-lg border border-border bg-card',
                className,
            )}
        >
            <div className="flex flex-none items-baseline justify-between border-b border-border px-3 py-2">
                <span className="text-[12px] font-semibold">{title}</span>
                {linkTo && linkLabel ? (
                    <Link
                        to="/retail-admin/$section"
                        params={{ section: linkTo }}
                        className="inline-flex items-center gap-0.5 text-[10.5px] text-primary hover:underline"
                    >
                        {linkLabel}
                        <ArrowRight className="size-3" aria-hidden />
                    </Link>
                ) : (
                    <span className="text-[10px] text-muted-foreground">{meta}</span>
                )}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </section>
    );
}

/** Conteo del encabezado de alertas; sin todas las fuentes revisadas no afirma un número. */
function alertsMeta(result: FloorAlertsResult): string {
    if (result.loading.length > 0) return 'revisando…';
    if (result.unchecked.length > 0) return '—';
    const count = result.alerts.length;
    return `${String(count)} activa${count === 1 ? '' : 's'}`;
}

export function HomeVlPage() {
    const sales = useHomeSalesKpis();
    const cancellations = useHomeCancellations7d();
    const stores = useTiendas('today', { rosterOptional: true });
    const sellers = useSellers('today', { rosterOptional: true });
    const stock = useInventarioCritical(true);
    const laCentral = useLaCentral();
    const { canMoveInventory } = useInventoryAbilities();
    const { hasPermission } = usePermissions();
    const canListPromotions = hasPermission(PROMOTIONS_PERMISSIONS.list);
    const promotionsConfigured = usePromotionsConfigured();
    const promotions = useSalePromotions({ archived: false, enabled: canListPromotions });

    const kpis = sales.data;
    const salesError = asError(sales.error);
    const cancellationsError = asError(cancellations.error);

    const goalUnavailable = Boolean(stores.error || stores.goalsError);
    // El % compara lo vendido por las tiendas con meta contra la suma de sus metas, no la red entera.
    const network =
        goalUnavailable || !stores.data
            ? null
            : networkGoal(stores.data.items, stores.data.activeStoreIds);
    const goalCents = network?.goalCents ?? null;
    const goalSoldCents = network?.soldCents ?? null;
    const goalReason = goalUnavailable
        ? GOAL_UNAVAILABLE
        : network && goalCents === null
          ? GOAL_NONE
          : network && goalSoldCents === null
            ? GOAL_SOLD_UNKNOWN
            : undefined;
    const goalScope =
        network && goalCents !== null && network.storesWithGoal < network.activeStores
            ? `${String(network.storesWithGoal)} de ${String(network.activeStores)} tiendas con meta`
            : null;
    const goal = networkGoalProgress(goalSoldCents, goalCents);
    const skShare = kpis ? surekeepMixShare(kpis) : null;
    const vsBaseline = kpis ? baselineChange(kpis) : null;
    const remaining = goalRemainingCents(goalSoldCents, goalCents);
    const surplus = goalSurplusCents(goalSoldCents, goalCents);
    const closing = hoursToClosing();
    const pace = goalPacePerHourCents(remaining, closing);

    const promotionsSource: FloorAlertSource<typeof promotions.items> =
        !canListPromotions || !promotionsConfigured
            ? { status: 'unavailable' }
            : promotions.error
              ? { status: 'error' }
              : promotions.isSuccess
                ? { status: 'ready', data: promotions.items }
                : { status: 'loading' };
    const floorAlerts = buildFloorAlerts(
        {
            stores: stores.goalsError
                ? { status: 'error' }
                : sourceOf(
                      stores,
                      stores.data && {
                          rows: stores.data.items,
                          activeStoreIds: stores.data.activeStoreIds,
                      },
                  ),
            sellers: sellers.goalsError
                ? { status: 'error' }
                : sourceOf(sellers, sellers.data?.items),
            cancellations: sourceOf(cancellations, cancellations.data?.items),
            promotions: promotionsSource,
        },
        new Date(),
    );

    // "Piso activo" es la fórmula del mock —tiendas y vendedores CON venta sobre el total—, y los
    // dos rankings ya tienen las filas cargadas. Solo se cae al silo si él lo empieza a mandar.
    const storeFloor = activeShare(stores.data?.items, (row) => row.unitsToday);
    const sellerFloor = activeShare(sellers.data?.items, (row) => row.unitsToday);
    const activeStores = kpis?.activeStoreCount ?? storeFloor.active;
    const activeSellers = kpis?.activeSellerCount ?? sellerFloor.active;
    // Las listas traen el padrón completo; los rankings muestran solo a quien vendió hoy.
    const storeRanking = useMemo(() => stores.data?.items.filter(hasSales), [stores.data]);
    const sellerRanking = useMemo(() => sellers.data?.items.filter(hasSales), [sellers.data]);

    return (
        <div className="flex h-full min-h-0 flex-col">
            {/* El mockup pone "Abrir La Central" en el topbar, junto al título de la pantalla. */}
            <ScreenControls>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                        laCentral.open({ action: 'stock' });
                    }}
                >
                    <FileText className="size-3" aria-hidden />
                    Abrir La Central
                </Button>
            </ScreenControls>
            <LaCentralDialog target={laCentral.target} onClose={laCentral.close} />

            {/*
              Barra de KPIs — `.kpi-bar` del mock: densa, una línea, pegada al topbar.
              El sangrado que cancela el `px-5 py-4` del `AppShell` ya vive en `<ScreenBand>`; acá
              se cancelaba a mano y cada pantalla nueva lo volvía a descubrir.
            */}
            <ScreenBand className="gap-0">
                <Kpi
                    label="Meta día"
                    value={goal === null ? '—' : `${String(Math.round(goal * 100))}%`}
                    sub={`de ${formatMoneyShort(goalCents)}`}
                    pending={goalReason}
                    pendingLabel={
                        goalUnavailable
                            ? 'no disponible'
                            : goalCents === null
                              ? 'sin meta'
                              : 'sin vendido'
                    }
                >
                    <ProgressMini pct={goal} />
                </Kpi>
                <Kpi
                    label="Vendido hoy"
                    value={kpis ? formatMoneyShort(kpis.soldTodayCents) : '—'}
                    sub={kpis ? `${String(kpis.unitsToday)} piezas` : '— piezas'}
                />
                <Kpi
                    label="Mix SK"
                    value={skShare === null ? '—' : `${String(Math.round(skShare * 100))}%`}
                    sub={kpis ? `${String(kpis.surekeepUnitsToday)} piezas SK` : '— piezas SK'}
                    valueClass="text-success"
                />
                <Kpi
                    label="Ticket prom."
                    value={kpis ? money(kpis.averageTicketCents) : '—'}
                    sub={
                        kpis && kpis.ticketChangeVs7d === null ? (
                            <span className="italic" title={TICKET_DELTA_NO_BASELINE}>
                                vs 7d sin línea base
                            </span>
                        ) : (
                            `vs 7d ${formatPercentDelta(kpis?.ticketChangeVs7d ?? null)}`
                        )
                    }
                />
                {/* El mock muestra el RATIO activo/total, no el activo suelto. */}
                <Kpi
                    label="Piso activo"
                    value={
                        activeStores === null
                            ? '—'
                            : `${String(activeStores)}/${String(storeFloor.total)}`
                    }
                    sub={
                        activeSellers === null
                            ? 'tiendas · — vend'
                            : `tiendas · ${String(activeSellers)}/${String(sellerFloor.total)} vend`
                    }
                    pending={
                        !stores.isPending && activeStores === null ? FLOOR_NO_SOURCE : undefined
                    }
                />
                <Kpi
                    label="Cancelaciones 7d"
                    value={cancellations.data?.count ?? '—'}
                    sub={
                        cancellationsError ? (
                            <span className="italic" title={cancellationsError.message}>
                                no disponible
                            </span>
                        ) : cancellations.data ? (
                            `${formatMoneyShort(cancellations.data.amountCents)} cancelado`
                        ) : (
                            '— cancelado'
                        )
                    }
                    valueClass="text-warning"
                />
                <Kpi label="Turno" value={`${String(closing)}h`} sub={nowHhMm()} />
            </ScreenBand>

            {/* `.home-grid` del mock: 2fr 1fr, gap y padding de 10 px, también a sangre completa. */}
            <div className="-mx-5 -mb-4 grid min-h-0 flex-1 grid-cols-[2fr_1fr] gap-2.5 bg-background p-2.5">
                {/* Columna izquierda: rankings */}
                <div className="flex min-h-0 flex-col gap-2">
                    <Card
                        title="Ranking tiendas hoy vs baseline 7d"
                        linkTo="tiendas"
                        linkLabel="Ver todas"
                        className="flex-1"
                    >
                        <HomeRankings
                            kind="stores"
                            stores={storeRanking}
                            isLoading={stores.isPending}
                            error={asError(stores.error)}
                            onRetry={stores.refetch}
                        />
                    </Card>
                    <Card
                        title="Top 10 vendedores hoy"
                        linkTo="vendedores"
                        linkLabel="Ver todos"
                        className="flex-1"
                    >
                        <HomeRankings
                            kind="sellers"
                            sellers={sellerRanking}
                            isLoading={sellers.isPending}
                            error={asError(sellers.error)}
                            onRetry={sellers.refetch}
                        />
                    </Card>
                </div>

                {/* Columna derecha: meta · mix · alertas · stock */}
                <div className="flex min-h-0 flex-col gap-2">
                    <Card
                        title="Meta del día · red completa"
                        meta={`${String(closing)} hrs para cierre`}
                        className="flex-none"
                    >
                        {salesError ? (
                            <ErrorState
                                className="m-2"
                                title="No se pudo cargar el pulso del piso"
                                description={salesError.message}
                                onRetry={() => {
                                    void sales.refetch();
                                }}
                            />
                        ) : sales.isPending || !kpis ? (
                            <div className="p-3">
                                <Skeleton lines={3} />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1.5 p-3">
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <span className="text-[18px] font-semibold tabular-nums">
                                            {goalCents === null
                                                ? money(kpis.soldTodayCents)
                                                : goalSoldCents === null
                                                  ? '—'
                                                  : money(goalSoldCents)}
                                        </span>
                                        <span className="text-[11px] text-muted-foreground">
                                            {' '}
                                            de{' '}
                                        </span>
                                        <span
                                            className="text-[12px] font-medium tabular-nums"
                                            title={goalReason}
                                        >
                                            {goalCents === null ? '—' : money(goalCents)}
                                        </span>
                                    </div>
                                    <span className="text-[12px] font-semibold">
                                        {goal === null ? '—' : `${String(Math.round(goal * 100))}%`}
                                    </span>
                                </div>
                                <div className="h-2 overflow-hidden rounded-full bg-muted">
                                    <div
                                        className="h-full rounded-full bg-success"
                                        style={{
                                            width: `${String(goal === null ? 0 : Math.min(Math.round(goal * 100), 100))}%`,
                                        }}
                                    />
                                </div>
                                {goalScope && (
                                    <div className="text-[10.5px] text-muted-foreground">
                                        {goalScope}
                                    </div>
                                )}
                                <div className="text-[10.5px] text-muted-foreground">
                                    {remaining === null ? (
                                        <span className="italic">{goalReason ?? '—'}</span>
                                    ) : surplus !== null ? (
                                        `¡Meta cumplida! Excedente: ${money(surplus)}`
                                    ) : pace === null ? (
                                        `Faltan ${money(remaining)} y el piso ya cerró.`
                                    ) : (
                                        `Faltan ${money(remaining)} en ${String(closing)}h · ${formatMoneyShort(pace)}/hr piso`
                                    )}
                                </div>
                                <div className="text-[10.5px]">
                                    {vsBaseline === null ? (
                                        <span className="italic text-muted-foreground">
                                            Sin línea base de la semana pasada.
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground">
                                            Baseline mismo día 7d:{' '}
                                            {kpis.baselineSameDayLastWeekUnits === null ? (
                                                <span
                                                    className="italic"
                                                    title={BASELINE_UNITS_NO_SOURCE}
                                                >
                                                    piezas sin fuente
                                                </span>
                                            ) : (
                                                `${String(kpis.baselineSameDayLastWeekUnits)} piezas`
                                            )}{' '}
                                            · {formatMoneyShort(kpis.baselineSameDayLastWeekCents)}{' '}
                                            <span
                                                className={cn(
                                                    'rounded px-1 py-px text-[9.5px] font-bold tabular-nums',
                                                    vsBaseline >= 0
                                                        ? 'bg-success-soft text-success'
                                                        : 'bg-destructive/10 text-destructive',
                                                )}
                                            >
                                                {formatPercentDelta(vsBaseline)}
                                            </span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </Card>

                    <Card
                        title="Mix financiera hoy"
                        meta={kpis ? `${String(kpis.unitsToday)} piezas · red completa` : ''}
                        className="flex-none"
                    >
                        <div className="px-3 py-2">
                            {sales.isPending || !kpis ? (
                                <Skeleton lines={4} />
                            ) : (
                                mixWithShare(kpis.mix)
                                    // El mock omite las financieras sin piezas hoy: una barra en 0
                                    // ocupa un renglón para no decir nada.
                                    .filter(({ slice }) => slice.salesCount > 0)
                                    .map(({ slice, share }) => (
                                        <div
                                            key={slice.financier}
                                            className="grid grid-cols-[60px_1fr_44px_40px] items-center gap-1.5 py-[3px] text-[11px]"
                                        >
                                            <span className="text-[10.5px]">
                                                {FINANCIER_LABEL[slice.financier]}
                                            </span>
                                            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                                                <div
                                                    className={cn(
                                                        'h-full rounded-full',
                                                        FINANCIER_BAR[slice.financier],
                                                    )}
                                                    style={{
                                                        width: `${String(Math.round(share * 100))}%`,
                                                    }}
                                                />
                                            </div>
                                            <span className="text-right font-semibold tabular-nums">
                                                {slice.salesCount}
                                            </span>
                                            <span className="text-right text-[10px] text-muted-foreground tabular-nums">
                                                {Math.round(share * 100)}%
                                            </span>
                                        </div>
                                    ))
                            )}
                        </div>
                    </Card>

                    <Card
                        title="Alertas del piso"
                        meta={alertsMeta(floorAlerts)}
                        className="flex-none"
                    >
                        <HomeAlerts result={floorAlerts} />
                    </Card>

                    <Card
                        title="Stock crítico financiable"
                        linkTo="inventario"
                        linkLabel="Ver inventario"
                        className="flex-1"
                    >
                        <HomeStock
                            items={stock.data?.items}
                            unavailable={stock.data?.unavailable ?? false}
                            reason={stock.data?.reason ?? null}
                            isLoading={stock.isPending}
                            error={asError(stock.error)}
                            onRetry={() => {
                                void stock.refetch();
                            }}
                            onOpenTransfer={canMoveInventory ? laCentral.open : null}
                        />
                    </Card>
                </div>
            </div>
        </div>
    );
}

/** Ícono reexportado para que los paneles compartan el mismo símbolo de alerta. */
export { AlertTriangle };
